import {
    AnimatePresence,
    animate,
    motion,
    useDragControls,
    useMotionValue,
    type AnimationPlaybackControls,
} from 'framer-motion';
import {
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
    type PointerEvent as ReactPointerEvent,
} from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { REPORT_COMMENT_MAX_LENGTH, ReportCommentSectionType } from '@/types/report-comments';
import { useReportCommentMutations } from '../hooks/use-report-comment-mutations';
import { CommentSectionOption } from '../types';

interface CreateCommentDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    reportId?: string | null;
    initialSectionId?: string | null;
    sectionOptions: CommentSectionOption[];
    onOpenAllComments: () => void;
}

const CENTER_SNAP_DISTANCE = 72;

export function CreateCommentDialog({
    open,
    onOpenChange,
    reportId,
    initialSectionId,
    sectionOptions,
    onOpenAllComments,
}: CreateCommentDialogProps) {
    const dialogBoxRef = useRef<HTMLDivElement | null>(null);
    const constraintsRef = useRef<HTMLDivElement | null>(null);
    const dragAnimationRef = useRef<AnimationPlaybackControls[]>([]);
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [content, setContent] = useState('');
    const [isDragging, setIsDragging] = useState(false);

    const dragControls = useDragControls();
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const commentsEnabled = Boolean(reportId);
    const { createCommentMutation } = useReportCommentMutations(reportId);

    const sectionOptionsRef = useRef(sectionOptions);
    useEffect(() => {
        sectionOptionsRef.current = sectionOptions;
    });

    useEffect(() => {
        if (!open) return;

        dragAnimationRef.current.forEach((animation) => animation.stop());
        dragAnimationRef.current = [];
        setSelectedSectionId(initialSectionId || sectionOptionsRef.current[0]?.id || '');
        setContent('');
        x.set(0);
        y.set(0);
    }, [initialSectionId, open, x, y]);

    const handleDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        if (window.innerWidth < 768) return;

        dragAnimationRef.current.forEach((animation) => animation.stop());
        dragAnimationRef.current = [];
        setIsDragging(true);
        dragControls.start(event, { snapToCursor: false });
    };

    const handleDragEnd = () => {
        setIsDragging(false);

        const currentX = x.get();
        const currentY = y.get();
        const distanceToCenter = Math.hypot(currentX, currentY);

        if (distanceToCenter > CENTER_SNAP_DISTANCE) {
            return;
        }

        dragAnimationRef.current = [
            animate(x, 0, {
                type: 'spring',
                stiffness: 420,
                damping: 34,
                mass: 0.75,
            }),
            animate(y, 0, {
                type: 'spring',
                stiffness: 420,
                damping: 34,
                mass: 0.75,
            }),
        ];
    };

    const handleCreateComment = async () => {
        const trimmedContent = content.trim();

        if (!trimmedContent || !selectedSectionId || !commentsEnabled) {
            return;
        }

        await createCommentMutation.mutateAsync({
            context: {
                type: 'section',
                section_type: selectedSectionId as ReportCommentSectionType,
            },
            content: trimmedContent,
        });

        onOpenChange(false);
    };

    const handleOpenAllComments = () => {
        onOpenChange(false);
        window.setTimeout(() => {
            onOpenAllComments();
        }, 0);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
            <DialogContent
                forceMount
                hideOverlay
                disableAnimation
                showCloseButton={false}
                onInteractOutside={(event: Event) => event.preventDefault()}
                className="pointer-events-none fixed inset-0 z-50 max-w-none translate-x-0 translate-y-0 border-none bg-transparent p-0 shadow-none"
            >
                <div
                    ref={constraintsRef}
                    className="pointer-events-none fixed inset-3 overflow-hidden md:inset-4"
                >
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <AnimatePresence>
                            {open && (
                                <motion.div
                                    initial={{
                                        opacity: 0,
                                        scale: 0.94,
                                        y: 12,
                                        filter: 'blur(6px)',
                                    }}
                                    animate={{
                                        opacity: 1,
                                        scale: 1,
                                        y: 0,
                                        filter: 'blur(0px)',
                                    }}
                                    exit={{
                                        opacity: 0,
                                        scale: 0.98,
                                        y: 8,
                                        filter: 'blur(4px)',
                                    }}
                                    transition={{
                                        duration: 0.28,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className="pointer-events-auto origin-center"
                                >
                                    <motion.div
                                        ref={dialogBoxRef}
                                        drag
                                        dragControls={dragControls}
                                        dragListener={false}
                                        dragMomentum={false}
                                        dragElastic={0}
                                        dragConstraints={constraintsRef}
                                        onDragEnd={handleDragEnd}
                                        style={{ x, y }}
                                        className="will-change-transform"
                                    >
                                        <div className="flex w-[min(480px,calc(100vw-2rem))] flex-col rounded-2xl border border-border/80 bg-card p-0 shadow-elevation-3">
                                            <div
                                                onPointerDown={handleDragStart}
                                                className={cn(
                                                    'cursor-grab rounded-t-2xl border-b border-border/70 px-5 py-2 active:cursor-grabbing',
                                                    isDragging && 'bg-muted',
                                                )}
                                            >
                                                <div className="flex justify-center">
                                                    <span
                                                        className={cn(
                                                            'h-1 w-10 rounded-full bg-muted transition-colors',
                                                            isDragging && 'bg-border-strong',
                                                        )}
                                                    />
                                                </div>
                                                <DialogTitle className="sr-only">
                                                    Novo comentário
                                                </DialogTitle>
                                                <DialogDescription className="sr-only">
                                                    Selecione a seção do laudo e escreva o
                                                    comentário que deseja adicionar.
                                                </DialogDescription>
                                            </div>

                                            <div className="space-y-4 px-5 py-4">
                                                <div className="space-y-2">
                                                    <Label>Seção</Label>
                                                    <Select
                                                        value={selectedSectionId || undefined}
                                                        onValueChange={setSelectedSectionId}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Selecione uma seção" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {sectionOptions.map((section) => (
                                                                <SelectItem
                                                                    key={section.id}
                                                                    value={section.id}
                                                                >
                                                                    {section.title}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {!commentsEnabled && (
                                                    <Alert variant="warning">
                                                        Salve o laudo primeiro para criar
                                                        comentários.
                                                    </Alert>
                                                )}

                                                <div className="space-y-2">
                                                    <Textarea
                                                        value={content}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLTextAreaElement>,
                                                        ) => setContent(event.target.value)}
                                                        placeholder="Escreva seu comentário..."
                                                        maxLength={REPORT_COMMENT_MAX_LENGTH}
                                                        disabled={
                                                            !commentsEnabled ||
                                                            createCommentMutation.isPending
                                                        }
                                                        className="min-h-[112px] resize-none bg-background"
                                                    />
                                                    <div className="flex items-center justify-end text-xs text-muted-foreground">
                                                        {content.length}/{REPORT_COMMENT_MAX_LENGTH}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/70 px-5 py-3">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleOpenAllComments}
                                                >
                                                    Ver todos comentários
                                                </Button>

                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => onOpenChange(false)}
                                                    >
                                                        Cancelar
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        onClick={handleCreateComment}
                                                        disabled={
                                                            !commentsEnabled ||
                                                            !selectedSectionId ||
                                                            !content.trim() ||
                                                            createCommentMutation.isPending
                                                        }
                                                    >
                                                        Comentar
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
